import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, Clock, CheckCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { getErrorMessage } from "@/lib/error-messages";

const RECAPTCHA_SITE_KEY = "6LdL7j8sAAAAAJleMiC4P6XTWXsFlOzPBDC84Rc7";

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const timeSlots = [
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
];

const BookDemo = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [date, setDate] = useState<Date>();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    phone: "",
    preferredTime: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!formData.fullName || !formData.email || !date || !formData.preferredTime) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      // Get reCAPTCHA token
      let recaptchaToken = "";
      try {
        recaptchaToken = await new Promise<string>((resolve, reject) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(RECAPTCHA_SITE_KEY, { action: "book_demo" })
              .then(resolve)
              .catch(reject);
          });
        });
      } catch (recaptchaError) {
        console.error("reCAPTCHA error:", recaptchaError);
        toast({
          title: "Verification failed",
          description: "Please try again or refresh the page.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Save to database
      const { error: dbError } = await supabase.from("demo_bookings").insert({
        full_name: formData.fullName,
        email: formData.email,
        company: formData.company || null,
        phone: formData.phone || null,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: formData.preferredTime,
        message: formData.message || null,
      });

      if (dbError) throw dbError;

      // Send confirmation email
      const { data, error: emailError } = await supabase.functions.invoke(
        "send-demo-confirmation",
        {
          body: {
            fullName: formData.fullName,
            email: formData.email,
            company: formData.company,
            phone: formData.phone,
            preferredDate: format(date, "yyyy-MM-dd"),
            preferredTime: formData.preferredTime,
            message: formData.message,
            recaptchaToken,
          },
        }
      );

      if (emailError) {
        console.error("Email error:", emailError);
        throw emailError;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setIsSuccess(true);
      toast({
        title: "Demo booked!",
        description: "We'll send you a confirmation email shortly.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24">
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-2xl mx-auto text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-primary-foreground" />
                </div>
                <h1 className="text-4xl font-bold mb-4">Demo Booked Successfully!</h1>
                <p className="text-xl text-muted-foreground mb-8">
                  Thank you, {formData.fullName}! We've sent a confirmation email to{" "}
                  <span className="text-foreground font-medium">{formData.email}</span>.
                </p>
                <Card className="p-6 text-left mb-8">
                  <h3 className="font-semibold mb-4">Your Demo Details:</h3>
                  <div className="space-y-2 text-muted-foreground">
                    <p>
                      <span className="font-medium text-foreground">Date:</span>{" "}
                      {date && format(date, "EEEE, MMMM d, yyyy")}
                    </p>
                    <p>
                      <span className="font-medium text-foreground">Time:</span>{" "}
                      {formData.preferredTime} (Brussels Time)
                    </p>
                  </div>
                </Card>
                <p className="text-muted-foreground">
                  Our team will reach out shortly to confirm the meeting details.
                </p>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Book Your Free Demo
              </h1>
              <p className="text-xl text-muted-foreground">
                See SmartCall AI in action. Schedule a personalized 15-minute demo with our team.
              </p>
            </div>
          </div>
        </section>

        {/* Booking Form */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-5 gap-12">
                {/* Benefits */}
                <div className="md:col-span-2">
                  <h2 className="text-2xl font-bold mb-6">What You'll Learn</h2>
                  <ul className="space-y-4">
                    {[
                      "How AI handles your calls 24/7",
                      "Seamless integration with your systems",
                      "Customization for your business needs",
                      "Real-time call analytics dashboard",
                      "Pricing tailored to your call volume",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Card className="p-6 mt-8 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock className="w-5 h-5 text-primary" />
                      <span className="font-semibold">15 Minutes</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Quick, focused demo tailored to your business. No lengthy sales pitch.
                    </p>
                  </Card>
                </div>

                {/* Form */}
                <div className="md:col-span-3">
                  <Card className="p-8 border-border">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fullName">Full Name *</Label>
                          <Input
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@company.be"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="company">Company</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your company"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+32 ..."
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Preferred Date *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !date && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {date ? format(date, "PPP") : "Pick a date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={date}
                                onSelect={setDate}
                                disabled={(date) =>
                                  date < new Date() ||
                                  date.getDay() === 0 ||
                                  date.getDay() === 6
                                }
                                initialFocus
                                className={cn("p-3 pointer-events-auto")}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div className="space-y-2">
                          <Label>Preferred Time *</Label>
                          <Select
                            value={formData.preferredTime}
                            onValueChange={(value) =>
                              setFormData({ ...formData, preferredTime: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlots.map((time) => (
                                <SelectItem key={time} value={time}>
                                  {time} (Brussels)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Additional Notes</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your business or any specific questions..."
                          rows={3}
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full"
                        variant="hero"
                        disabled={isLoading}
                      >
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Book My Demo
                      </Button>

                      <p className="text-xs text-center text-muted-foreground">
                        By booking, you agree to our{" "}
                        <a href="/privacy-policy" className="underline hover:text-foreground">
                          Privacy Policy
                        </a>
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BookDemo;
